import {
  typedInfiniteQuery,
  typedMutation,
  typedQuery,
} from "../types/typedQueries";
import { PaginationT } from "../types/services";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { bareInstance, paginationReponseToGeneral } from "./common";
import { DeleteNewsT, NewsDTO, NewsResponseT, OneNewsT, UpdateNewsT } from "../types/news";
import useToastes from "../../hooks/useToastes";
import { LayoutsE, PathsE, PathsParams } from "../../routes";
import useTypedHistory from "../../hooks/useTypedHistory";

const ONE_NEWS_KEY = "one-news";
const MODULE_NAME = 'news';

interface Props {
  domain: string;
  token: string;
}

export const useGetNewsService = ({ domain, token }: Props) => {
  return {
    useGetNews: typedInfiniteQuery<NewsResponseT>((body) => {
      const params = body?.params;
      return useInfiniteQuery({
        queryKey: [MODULE_NAME, token, domain, params],
        queryFn: () =>
          bareInstance.get<
            unknown,
            { items: NewsDTO[]; pagination: PaginationT }
          >(`${domain}/${MODULE_NAME}/`, {
            headers: { "x-service-token": token },
            params,
          }),
        enabled: !!domain,
        initialPageParam: 1,
        getNextPageParam: () => null,
        getPreviousPageParam: () => null,
        select: (data) => paginationReponseToGeneral(data as FixMeLater),
      });
    }),

    useGetOneNews: typedQuery<OneNewsT>(({ pathParams: { id } }) => {
      return useQuery({
        queryKey: [ONE_NEWS_KEY, id],
        queryFn: () =>
          bareInstance.get(`${domain}/${MODULE_NAME}/${id}`, {
            headers: { "x-service-token": token },
          }),
        enabled: !!id || !!domain || !!token,
      });
    }),

    useUpdateOneNews: typedMutation<UpdateNewsT>(
      ({ pathParams: { id, serviceId }, params }) => {
        const { successToast } = useToastes();
        const queryCache = useQueryClient();
        const push = useTypedHistory<PathsParams[PathsE.GROUPS]>();
        return useMutation({
          mutationFn: (data) =>
            bareInstance.put(`${domain}/${MODULE_NAME}/${id}`, data, { headers: { "x-service-token": token } }),
          onSuccess: () => {
            successToast("News was successfully updated");
            queryCache.invalidateQueries({ queryKey: [MODULE_NAME] });
            queryCache.invalidateQueries({ queryKey: [id, ONE_NEWS_KEY] });
            push(PathsE.GROUPS, LayoutsE.DASHBOARD, { serviceId}, params);
          },
        });
      },
    ),
    useDeleteNews: typedMutation<DeleteNewsT>(() => {
      const { successToast } = useToastes();
      const queryCache = useQueryClient();

      return useMutation({
        mutationFn: ({ id }) =>
          bareInstance.delete(
            `${domain}/${MODULE_NAME}/${id}`,
            { headers: { "x-service-token": token } },
          ),
        onSuccess: () => {
          successToast("News was successfully deleted");
          queryCache.invalidateQueries({ queryKey: [MODULE_NAME] });
        },
      })
    }),
  };
};
